import * as React from "react";
import { Loader } from "@/components/ui/loader";
import { FilesSelectTreeSearch } from "./search";
import { FilesSelectTreeRow } from "./row";
import { FilesSelectTreeBreadcrumb } from "./breadcrumb";
import { useSearchQuery, useGetFileDetailsQuery } from "@/api/files";
import { Item, FolderItem } from "@/types/fse";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SortDropdown } from "@/components/sortDropdown";
import { useSortedItems } from "@/hooks/use-sorted-items";
import { SearchMode, mapSearchModeToApi } from "@/views/drive/search/types";
import { getPathForFolder, type PathSegment } from "./navigation";
import { getSearchEntryTypes, shouldShowItem } from "./helpers";

export interface FilesSelectTreeProps {
  onAction: (item: Item) => void;
  actionButton?: React.ReactNode | ((item: Item) => React.ReactNode);
  folderActionButton?: React.ReactNode | ((item: Item) => React.ReactNode);
  showFiles?: boolean;
  showFolders?: boolean;
  filterCurrentFolder?: string;
  className?: string;
}

export const FilesSelectTree: React.FC<FilesSelectTreeProps> = ({
  onAction,
  actionButton,
  folderActionButton,
  showFiles = true,
  showFolders = true,
  filterCurrentFolder,
  className = "",
}) => {
  const [query, setQuery] = React.useState("");
  const [currentFolderId, setCurrentFolderId] = React.useState<string | null>(null);
  const [path, setPath] = React.useState<PathSegment[]>([]);
  const [activeTab, setActiveTab] = React.useState<"my-files" | "shared">("my-files");
  const [searchMode, setSearchMode] = React.useState<SearchMode>("standard");
  const searchEntryTypes = React.useMemo(
    () => getSearchEntryTypes({ showFiles, showFolders }),
    [showFiles, showFolders],
  );

  // Reset navigation when switching tabs
  React.useEffect(() => {
    setQuery("");
    setSearchMode("standard");
    setCurrentFolderId(null);
    setPath([]);
  }, [activeTab]);

  // Search mode follows the same visible entry types as browse mode.
  const { isLoading: isSearchLoading, data: searchData = [] } = useSearchQuery(
    {
      query,
      fse_id: currentFolderId || undefined,
      entry_type: searchEntryTypes,
      only_shared: activeTab === "shared",
      search_mode: mapSearchModeToApi(searchMode),
    },
    { skip: !query || searchEntryTypes?.length === 0 },
  );

  // Browse mode
  // Only use only_shared when at root level (no folderId) in shared tab
  const { isLoading: isBrowseLoading, data: browseData } = useGetFileDetailsQuery(
    {
      fse_id: currentFolderId || undefined,
      only_shared: activeTab === "shared" && !currentFolderId ? true : undefined,
    },
    { skip: !!query },
  );

  React.useEffect(() => {
    if (query || !browseData || browseData.type !== "folder") return;

    const isCurrentBrowseData = currentFolderId ? browseData.id === currentFolderId : browseData.parent_fs_id === null;
    if (!isCurrentBrowseData) return;

    setPath(getPathForFolder(browseData));
  }, [browseData, currentFolderId, query]);

  const isLoading = query ? isSearchLoading : isBrowseLoading;

  // First, compute filtered items (before sorting)
  const filteredItems: Item[] = React.useMemo(() => {
    let items: Item[] = [];

    if (query) {
      const searchItems: Item[] = Array.isArray(searchData) ? searchData : [];
      items = searchItems.filter(item => shouldShowItem(item, { showFiles, showFolders, filterCurrentFolder }));
    } else {
      // Browse mode: get items from current folder
      if (!browseData) return [];

      if (browseData.type === "folder") {
        const folderData = browseData as FolderItem;
        const allItems = Object.values(folderData.items);

        items = allItems.filter(item => shouldShowItem(item, { showFiles, showFolders, filterCurrentFolder }));
      }
    }

    return items;
  }, [query, searchData, browseData, showFiles, showFolders, filterCurrentFolder]);

  // Use the sorting hook with filtered items
  const {
    sortedItems: allSortedItems,
    currentSort,
    setCurrentSort,
  } = useSortedItems(filteredItems, "files-select-tree-sorted-items-config");

  // Separate folders and files, keeping the sorted order
  const items: Item[] = React.useMemo(() => {
    const folders = allSortedItems.filter(item => item.type === "folder");
    const files = allSortedItems.filter(item => item.type === "file");

    return [...folders, ...files];
  }, [allSortedItems]);

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
  };

  const handleClear = () => {
    setQuery("");
  };

  const handleFolderClick = (folder: FolderItem) => {
    setQuery("");
    setCurrentFolderId(folder.id);
    setPath(getPathForFolder(folder));
  };

  const handleNavigate = (folderId: string | null) => {
    if (folderId === null) {
      // Navigate to root
      setCurrentFolderId(null);
      setPath([]);
    } else {
      // Navigate to specific folder in path
      const index = path.findIndex(p => p.id === folderId);
      if (index !== -1) {
        setCurrentFolderId(folderId);
        setPath(path.slice(0, index + 1));
      }
    }
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={value => setActiveTab(value as "my-files" | "shared")}
      className={`flex flex-grow flex-col overflow-hidden ${className}`}
    >
      <TabsList className="mb-4 w-full justify-start">
        <TabsTrigger value="my-files">My Files</TabsTrigger>
        <TabsTrigger value="shared">Shared with me</TabsTrigger>
      </TabsList>

      <TabsContent value={activeTab} className="mt-0 flex flex-grow flex-col overflow-hidden">
        <FilesSelectTreeSearch
          query={query}
          onSearch={handleSearch}
          onClear={handleClear}
          searchMode={searchMode}
          onModeChange={setSearchMode}
        />

        <div className="mb-4 flex items-center justify-between">
          {!query && <FilesSelectTreeBreadcrumb path={path} onNavigate={handleNavigate} />}
          <div className="ml-auto">
            <SortDropdown
              onSortChange={setCurrentSort}
              currentSort={currentSort}
              fields={[
                { key: "name", label: "Name" },
                { key: "created_at", label: "Created Date" },
              ]}
              className="mb-0"
            />
          </div>
        </div>

        {query && !items.length && !isLoading && <p className="text-md text-center text-gray-500">No items found.</p>}

        {isLoading && <Loader />}

        {!isLoading && !!items.length && (
          <div className="flex flex-grow flex-col overflow-y-scroll">
            {items.map(item => (
              <FilesSelectTreeRow
                key={item.id}
                item={item}
                onAction={onAction}
                onFolderClick={handleFolderClick}
                actionButton={actionButton}
                folderActionButton={folderActionButton}
                showFolders={showFolders}
              />
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};
